import { PersonalizationCriteria } from '@lib/types/component-variation'
import { normalizeString } from '@lib/utils/strings'

/**
 * Make sure all keys and values are normalized to lowercase and comma-separated strings
 */
function normalizeUrlParams(params: Record<string, string | string[] | undefined>): Record<string, string> {
  return Object.entries(params).reduce<Record<string, string>>((normalized, [key, value]) => {
    if (!value) {
      return normalized
    }

    const normalizedKey = normalizeString(key)
    const normalizedValue = Array.isArray(value) ? value.map(normalizeString).join(',') : normalizeString(value)

    return {
      ...normalized,
      [normalizedKey]: normalizedValue,
    }
  }, {})
}

/**
 * Match personalization criteria against the current URL parameters
 */
export function matchPersonalizationCriteria(
  criteria: PersonalizationCriteria,
  urlParams: Record<string, string | string[] | undefined>
): boolean {
  const normalizedParams = normalizeUrlParams(urlParams)

  return criteria.urlParams.every(({ parameter, value, operator }) => {
    const normalizedParam = normalizedParams[normalizeString(parameter)]

    if (!normalizedParam) {
      return false
    }
    const normalizedValue = normalizeString(value)

    switch (operator) {
      case 'equals':
        return normalizedParam === normalizedValue
      case 'contains':
        return normalizedParam.includes(normalizedValue)
      case 'startsWith':
        return normalizedParam.startsWith(normalizedValue)
      case 'oneOf': {
        const criteriaValues = value.split(',').map(normalizeString)
        const paramValues = normalizedParam.split(',').map(normalizeString)
        return criteriaValues.some((v) => paramValues.includes(v))
      }
      case 'everyOf': {
        const criteriaValues = value.split(',').map(normalizeString)
        const paramValues = normalizedParam.split(',').map(normalizeString)
        return criteriaValues.every((v) => paramValues.includes(v))
      }
      default:
        return false
    }
  })
}
